import { useEffect, useState } from 'react';
import { useMediaQuery } from '@mui/material';
// ─── Day counts ─────────────────────────────────────────────────────────────────
// One helper for every place a number of days is printed (chips, summary,
// tables, tooltips): 0.25 -> ¼, 0.5 -> ½, 1.5 -> 1½, 9 -> 9.
export function fmtDays(n) {
    const v = Number(n);
    if (!Number.isFinite(v))
        return '0';
    return String(Math.round(v * 100) / 100);
}
/** "1.5 days", "0.5 day", "1 day" */
export const fmtDur = (n) => `${fmtDays(n)} ${Number(n) > 1 ? 'days' : 'day'}`;
// ─── Leave type labels ──────────────────────────────────────────────────────────
const CODE_ALIAS = { COMP: 'CO', UNPAID: 'UL' };
/** Short code shown on chips and bars: EL, CL, SL, CO, UL. */
export const shortCode = (code) => {
    const c = (code || '').toUpperCase();
    return CODE_ALIAS[c] || c.slice(0, 3);
};
/** "Earned Leave" -> "Earned" (used in the muted greeting line). */
export const shortTypeName = (name) => (name || '').replace(/\s+leave$/i, '');
export const isCompOffType = (t) => !!t && (/^comp$/i.test(t.code || '') || /comp(ensatory)?[ -]?off/i.test(t.name || ''));
export const isUnpaidTypeLike = (t) => !!t && (/^unpaid$/i.test(t.code || '') || (t.isPaid === false && !t.requiresAllocation));
// ─── Dates (plain 'YYYY-MM-DD' calendar dates — never timezone-shifted) ──────────
/** A leave's calendar date as the backend defines it: shift into IST first. */
export const toCalendarDate = (iso) => new Date(new Date(iso).getTime() + 5.5 * 3600000).toISOString().slice(0, 10);
const dayNum = (s) => Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10)) / 86400000;
const asDate = (s) => new Date(dayNum(s) * 86400000);
const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const fmtYmd = (s, withYear = false) => {
    const d = asDate(s);
    return `${d.getUTCDate()} ${MON[d.getUTCMonth()]}${withYear ? ` ${d.getUTCFullYear()}` : ''}`;
};
/** "Sat 3 and Sun 4 Nov are not counted" */
export function fmtUncounted(days) {
    if (!days.length)
        return '';
    if (days.length > 6)
        return `${days.length} weekends and holidays in this range are not counted`;
    const parts = days.map(d => ({ wd: WD[asDate(d.date).getUTCDay()], day: asDate(d.date).getUTCDate(), mon: MON[asDate(d.date).getUTCMonth()], name: d.reason === 'holiday' ? d.name : undefined }));
    const sameMonth = parts.every(p => p.mon === parts[0].mon);
    const labels = parts.map(p => `${p.wd} ${p.day}${sameMonth ? '' : ` ${p.mon}`}${p.name ? ` (${p.name})` : ''}`);
    const joined = labels.length === 1 ? labels[0] : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
    return `${joined}${sameMonth ? ` ${parts[0].mon}` : ''} ${days.length === 1 ? 'is' : 'are'} not counted`;
}
// ─── Responsive hooks ───────────────────────────────────────────────────────────
/** Phones: 0–599px. */
export const useIsMobile = () => useMediaQuery('(max-width:599.95px)');
/** Wide desktops: the calendar gets its right rail from 1100px. */
export const useIsWide = () => useMediaQuery('(min-width:1100px)');
/**
 * Height of the area the user can actually see. On phones the soft keyboard
 * shrinks the visual viewport but not the layout viewport, so a bottom bar
 * positioned against the layout viewport ends up behind the keyboard. Full-
 * screen sheets size themselves to this instead, which keeps Send in view.
 */
export function useVisibleHeight() {
    const read = () => (typeof window === 'undefined' ? 800 : Math.round(window.visualViewport?.height ?? window.innerHeight));
    const [h, setH] = useState(read);
    useEffect(() => {
        const vv = window.visualViewport;
        const on = () => setH(read());
        vv?.addEventListener('resize', on);
        vv?.addEventListener('scroll', on);
        window.addEventListener('resize', on);
        return () => { vv?.removeEventListener('resize', on); vv?.removeEventListener('scroll', on); window.removeEventListener('resize', on); };
    }, []);
    return h;
}
